import { useMemo } from 'react';
import { clsx } from 'clsx';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  User,
  Building2,
} from 'lucide-react';

interface WebResult {
  doctorId?: string;
  type?: 'doctor' | 'institution';
  name: string;
  title?: string;
  institution?: string;
  city?: string;
  canton?: string;
  specialty?: string;
  matchScore?: number;
}

interface SearchExecution {
  id: string;
  blindCode: string;
  status: string;
  searchResults?: {
    webResults?: WebResult[];
    searchSummary?: string;
  };
}

interface ProviderOverlapMatrixProps {
  executions: SearchExecution[];
}

interface ProviderEntry {
  name: string;
  type: 'doctor' | 'institution' | 'unknown';
  specialty?: string;
  institution?: string;
  city?: string;
  appearances: Map<string, number | null>; // blindCode -> rank (1-based, null if not present)
  overlapCount: number;
  totalExecutions: number;
}

function getOverlapStatus(count: number, total: number): 'full' | 'partial' | 'single' {
  if (count === total) return 'full';
  if (count > 1) return 'partial';
  return 'single';
}

function getOverlapColor(status: 'full' | 'partial' | 'single'): string {
  switch (status) {
    case 'full':
      return 'text-green-600 bg-green-50';
    case 'partial':
      return 'text-amber-600 bg-amber-50';
    case 'single':
      return 'text-gray-400 bg-gray-50';
  }
}

function getOverlapIcon(status: 'full' | 'partial' | 'single') {
  switch (status) {
    case 'full':
      return CheckCircle;
    case 'partial':
      return AlertTriangle;
    case 'single':
      return XCircle;
  }
}

export function ProviderOverlapMatrix({ executions }: ProviderOverlapMatrixProps) {
  // Track iterations with results vs empty
  const iterationsWithResults = useMemo(
    () => executions.filter((exec) => (exec.searchResults?.webResults || []).length > 0),
    [executions]
  );
  const iterationsWithResultsCount = iterationsWithResults.length;
  const emptyIterationsCount = executions.length - iterationsWithResultsCount;

  // Build provider map
  const providerData = useMemo(() => {
    const providers = new Map<string, ProviderEntry>();
    // Use effective iterations (those with results) for overlap calculation
    const effectiveTotal = Math.max(iterationsWithResultsCount, 1);

    executions.forEach((exec) => {
      const results = exec.searchResults?.webResults || [];
      results.forEach((result, index) => {
        // Use doctorId if available, otherwise use name as key
        const key = result.doctorId || `${result.name}-${result.institution || ''}`;

        if (!providers.has(key)) {
          providers.set(key, {
            name: result.name,
            type: result.type || 'unknown',
            specialty: result.specialty,
            institution: result.institution,
            city: result.city,
            appearances: new Map(),
            overlapCount: 0,
            totalExecutions: effectiveTotal, // Use effective total for overlap calculation
          });
        }

        const entry = providers.get(key)!;
        entry.appearances.set(exec.blindCode, index + 1); // 1-based rank
      });
    });

    // Calculate overlap counts
    providers.forEach((entry) => {
      entry.overlapCount = entry.appearances.size;
    });

    // Sort by overlap count (descending), then by average rank
    return Array.from(providers.values()).sort((a, b) => {
      if (b.overlapCount !== a.overlapCount) {
        return b.overlapCount - a.overlapCount;
      }
      // Secondary sort by average rank (lower is better)
      const avgRankA =
        Array.from(a.appearances.values())
          .filter((r): r is number => r !== null)
          .reduce((sum, r) => sum + r, 0) / a.overlapCount;
      const avgRankB =
        Array.from(b.appearances.values())
          .filter((r): r is number => r !== null)
          .reduce((sum, r) => sum + r, 0) / b.overlapCount;
      return avgRankA - avgRankB;
    });
  }, [executions, iterationsWithResultsCount]);

  // Get blind codes for column headers
  const blindCodes = executions.map((e) => e.blindCode);

  if (executions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
        <p>No search executions to compare</p>
      </div>
    );
  }

  if (providerData.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
        <p>No providers found in search results</p>
      </div>
    );
  }

  // Calculate summary metrics based on iterations WITH results
  const effectiveTotal = Math.max(iterationsWithResultsCount, 1);
  const fullOverlapCount = providerData.filter((p) => p.overlapCount >= effectiveTotal).length;
  const partialOverlapCount = providerData.filter(
    (p) => p.overlapCount > 1 && p.overlapCount < effectiveTotal
  ).length;
  const singleCount = providerData.filter((p) => p.overlapCount === 1).length;

  // Determine badge labels based on whether there are empty iterations
  const hasEmptyIterations = emptyIterationsCount > 0;
  const allRunsLabel = hasEmptyIterations
    ? `${fullOverlapCount} in all ${iterationsWithResultsCount} runs with results`
    : `${fullOverlapCount} in all runs`;

  return (
    <div className="space-y-4">
      {/* Summary badges */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 text-green-700 text-sm font-medium">
          <CheckCircle className="h-4 w-4" />
          {allRunsLabel}
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 text-sm font-medium">
          <AlertTriangle className="h-4 w-4" />
          {partialOverlapCount} in some runs
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 text-sm font-medium">
          <XCircle className="h-4 w-4" />
          {singleCount} unique to one run
        </div>
        {hasEmptyIterations && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-100 text-red-700 text-sm font-medium">
            <XCircle className="h-4 w-4" />
            {emptyIterationsCount} run{emptyIterationsCount > 1 ? 's' : ''} with no results
          </div>
        )}
      </div>

      {/* Matrix table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Provider
              </th>
              {blindCodes.map((code) => (
                <th
                  key={code}
                  className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider"
                >
                  <span className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                    {code}
                  </span>
                </th>
              ))}
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Overlap
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {providerData.map((provider, idx) => {
              const status = getOverlapStatus(provider.overlapCount, provider.totalExecutions);
              const StatusIcon = getOverlapIcon(status);

              return (
                <tr
                  key={idx}
                  className={clsx(
                    'hover:bg-gray-50',
                    status === 'full' && 'bg-green-50/50',
                    status === 'partial' && 'bg-amber-50/50'
                  )}
                >
                  {/* Provider info */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {provider.type === 'institution' ? (
                        <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      ) : (
                        <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{provider.name}</p>
                        <p className="text-xs text-gray-500">
                          {[provider.specialty, provider.city].filter(Boolean).join(' • ')}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Rank in each execution */}
                  {blindCodes.map((code) => {
                    const rank = provider.appearances.get(code);
                    return (
                      <td key={code} className="px-4 py-3 text-center">
                        {rank !== undefined && rank !== null ? (
                          <span
                            className={clsx(
                              'inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold',
                              rank <= 3
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-gray-100 text-gray-600'
                            )}
                          >
                            #{rank}
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                    );
                  })}

                  {/* Overlap indicator */}
                  <td className="px-4 py-3 text-center">
                    <span
                      className={clsx(
                        'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
                        getOverlapColor(status)
                      )}
                    >
                      <StatusIcon className="h-3 w-3" />
                      {provider.overlapCount}/{provider.totalExecutions}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-4 p-3 rounded-lg bg-gray-50 text-xs text-gray-600">
        <strong>Legend:</strong> Each cell shows the ranking (#1, #2, etc.) of the provider in that
        iteration's search results. Providers appearing in all iterations indicate consistent
        recommendations.
        {hasEmptyIterations && (
          <span className="block mt-1 text-amber-700">
            Note: {emptyIterationsCount} iteration{emptyIterationsCount > 1 ? 's' : ''} returned no
            results (shown as "-" for all providers). Overlap is calculated from the{' '}
            {iterationsWithResultsCount} iteration{iterationsWithResultsCount > 1 ? 's' : ''} with
            results.
          </span>
        )}
      </div>
    </div>
  );
}

export type { SearchExecution, WebResult, ProviderEntry };
